/*!
 * Copyright (c) 2023 Bluemate B.V.
 */

@use '../../../../../style/dimensions';
@use '../../../../../style/common-variables' as commons;

.dvrd-date-picker {
  display: flex;
  flex-direction: column;
  color: commons.$color-blue-text;
  position: relative;

  .field-label {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
    display: block;
    transition: color 0.1s ease-in-out;
  }

  .value-container {
    border: 1px solid #d9d9d9;
    border-radius: dimensions.$radius-md;
    padding: 0.75rem;
    transition: border-color 0.1s ease-in-out;
    position: relative;
    cursor: pointer;

    .value {
      transition: color 0.1s ease-in-out;
      padding-right: 1.5rem;

      &.placeholder {
        color: commons.$color-gray-4;
      }
    }

    .calendar-icon {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .error-label {
    position: absolute;
    bottom: 0;
    transform: translateY(1rem);
    left: 0;
    color: red;
    font-size: 0.8rem;
    visibility: hidden;
    opacity: 0;
    transition:
      visibility 0.1s ease-in-out,
      opacity 0.1s ease-in-out;
  }

  &.error {
    margin-bottom: 1rem;
    color: red;

    .value-container {
      border-color: red;
    }

    .error-label {
      visibility: visible;
      opacity: 1;
    }
  }

  &.disabled {
    &,
    * {
      cursor: not-allowed;
    }

    .value-container {
      color: gray;
    }
  }
}

// The calendar popup is portalled next to the field (DvrdDatePicker) or rendered
// by BluemateInput's `date` variant, so its styles must not depend on either wrapper.
.bluemate-date-picker-popup {
  @include commons.backgroundShadow2;
  --day-size: 26px;
  background-color: white;
  display: grid;
  grid-template-columns: 1fr;
  width: calc(var(--day-size) * 7 + 0.5rem * 6 + 1rem);
  max-width: 100vw;
  color: commons.$color-blue-text;

  .switcher,
  .current-date-container,
  .days-container,
  .today-container,
  .actions-container {
    padding: 0.5rem;
  }

  .switcher {
    display: grid;
    justify-content: center;
    background-color: commons.$color-blue-1;
    color: white;
    align-items: center;
    grid-column-gap: 0.5rem;

    .switcher-label {
      cursor: pointer;
      user-select: none;
      border-bottom: 1px solid transparent;
      transition: border-color 0.2s ease-in-out;
      text-align: center;
      justify-self: center;

      &:hover {
        border-bottom-color: currentColor;
      }
    }

    .switcher-icon {
      cursor: pointer;
      font-size: 0.9rem;
      visibility: hidden;
      opacity: 0;
      transition:
        color 0.2s ease-in-out,
        opacity 0.2s ease-in-out,
        visibility 0.2s ease-in-out;
      padding: 0.25rem;
      user-select: none;

      &:hover {
        color: commons.$color-gray-7;
      }
    }

    &.year {
      grid-template-columns: fit-content(100%) 2rem fit-content(100%);
      padding-bottom: 0.25rem;
    }

    &.month {
      grid-template-columns: fit-content(100%) 4.5rem fit-content(100%);
      padding-top: 0.25rem;
    }

    &:hover {
      .switcher-icon {
        visibility: visible;
        opacity: 1;
      }
    }

    &:first-child {
      border-radius: 0.5rem 0.5rem 0 0;
    }
  }

  .current-date-container {
    position: relative;
    display: flex;
    justify-content: center;

    .current-date-label {
      font-size: 0.9rem;
      color: commons.$color-gray-3;
    }

    .tooltip-container {
      @include commons.centerY;
      position: absolute;
      right: 0.5rem;

      .tooltip-icon {
        color: commons.$color-gray-3;
        font-size: 0.8rem;
        cursor: help;
      }

      .tooltip {
        position: absolute;
        left: 0;
        bottom: 0;
        transform: translateY(100%);
        background-color: white;
        box-shadow: none;
        visibility: hidden;
        opacity: 0;
        border-radius: 0.25rem !important;
        padding: 0.25rem;
        transition:
          visibility 0.2s ease-in-out,
          opacity 0.2s ease-in-out,
          box-shadow 0.2s ease-in-out;
        display: grid;
        grid-template-columns: auto;
        grid-row-gap: 0.5rem;
        border: 1px solid commons.$color-gray-4;

        .tooltip-label {
          justify-self: center;
          font-size: 0.8rem;
          color: commons.$color-gray-3;
        }

        .key {
          display: grid;
          grid-column-gap: 0.5rem;
          grid-template-columns: 9.4rem fit-content(100%);
          align-items: center;
          justify-items: end;

          .directives {
            display: grid;
            grid-template-columns: repeat(3, fit-content(100%));
            grid-column-gap: 0.5rem;
            align-items: center;

            &.full {
              grid-template-columns: repeat(5, fit-content(100%));
            }
          }

          .shortcut,
          .directive {
            font-size: 0.8rem;
            color: commons.$color-gray-3;
            white-space: nowrap;
          }

          .shortcut {
            font-size: 0.7rem;
            padding: 0.1rem 0.25rem;
            border: 1px solid commons.$color-gray-3;

            &.text {
              padding: 0 0.25rem;
            }
          }

          .directive {
            display: block;
          }
        }
      }

      &:hover {
        .tooltip {
          @include commons.backgroundShadow;
          visibility: visible;
          opacity: 1;
        }
      }
    }
  }

  .days-container {
    display: grid;
    grid-template-columns: repeat(7, var(--day-size));
    grid-gap: 0.5rem;

    .day {
      width: var(--day-size);
      height: var(--day-size);
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      cursor: pointer;
      transition:
        color 0.2s ease-in-out,
        background-color 0.2s ease-in-out;

      .day-label {
        font-size: 14px;
      }

      &.no-select,
      &.hide {
        pointer-events: none;
      }

      &.hide {
        visibility: hidden;
      }

      &.pad {
        color: commons.$color-gray-4;
      }

      &.selected {
        background-color: commons.$color-blue-1 !important;
        color: white;
      }

      &:hover {
        background-color: rgba(commons.$color-blue-1, 0.2);
      }

      &.disabled {
        pointer-events: none;
        color: commons.$color-gray-3;
      }
    }

    .extra-row {
      grid-column-start: span 7;
      height: 24px;
    }
  }

  .actions-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .action {
      padding: 0.5rem;

      .dvrd-button-label {
        font-size: 0.9rem;
      }
    }
  }

  &.switch-mount {
    .switcher {
      .switcher-icon {
        visibility: visible !important;
        opacity: 1 !important;
        transition: none !important;
      }
    }
  }
}
